import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtomValue } from "jotai";
import { apiCall } from "../lib/api-client.js";
import { ApiError, type Organization } from "../lib/api-types.js";
import { authAtom } from "../lib/auth-store.js";
import { PageHeader } from "../components/PageHeader.js";
import { InlineLoading } from "../components/Spinner.js";
import { ErrorBanner, InfoBanner } from "../components/ErrorBanner.js";
import { cn } from "../lib/cn.js";

interface DashboardStats {
  total: number;
  active: number;
  suspended: number;
  archived: number;
  feishuConfigured: number;
}

function computeStats(orgs: Organization[]): DashboardStats {
  return {
    total: orgs.length,
    active: orgs.filter((o) => o.status === "active").length,
    suspended: orgs.filter((o) => o.status === "suspended").length,
    archived: orgs.filter((o) => o.status === "archived").length,
    feishuConfigured: orgs.filter((o) => Boolean(o.feishu?.appId)).length,
  };
}

function StatCard({
  label,
  value,
  hint,
  className,
}: {
  label: string;
  value: number;
  hint?: string;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={cn(
        "rounded-lg border border-dosia-border bg-dosia-bg-light p-4",
        className,
      )}
    >
      <p className="text-sm text-dosia-text-secondary">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-dosia-text">{value}</p>
      {hint ? (
        <p className="mt-1 text-xs text-dosia-text-dim">{hint}</p>
      ) : null}
    </div>
  );
}

export function Dashboard(): JSX.Element {
  const { t } = useTranslation();
  const auth = useAtomValue(authAtom);
  const [orgs, setOrgs] = useState<Organization[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!auth) return;
    void (async () => {
      try {
        const data = await apiCall<Organization[]>(
          "GET",
          "/api/organizations?includeArchived=true",
          { token: auth.token },
        );
        if (!cancelled) {
          setOrgs(data);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        const msg =
          err instanceof ApiError ? err.message : t("admin.dashboard.loadError");
        setError(msg);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  if (!orgs && !error) {
    return (
      <div>
        <PageHeader
          title={t("admin.dashboard.title")}
          subtitle={t("admin.dashboard.subtitle")}
        />
        <InlineLoading />
      </div>
    );
  }

  const stats = computeStats(orgs ?? []);
  const phaseHint =
    stats.total === 0
      ? t("admin.dashboard.empty")
      : t("admin.dashboard.phaseHint", {
          configured: stats.feishuConfigured,
          total: stats.total,
        });

  return (
    <div>
      <PageHeader
        title={t("admin.dashboard.title")}
        subtitle={t("admin.dashboard.subtitle")}
      />

      {error ? (
        <div className="mb-4">
          <ErrorBanner>{error}</ErrorBanner>
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label={t("admin.dashboard.stat.total.label")}
          value={stats.total}
          hint={t("admin.dashboard.stat.total.hint", {
            active: stats.active,
            suspended: stats.suspended,
            archived: stats.archived,
          })}
        />
        <StatCard
          label={t("admin.dashboard.stat.feishu.label")}
          value={stats.feishuConfigured}
          hint={t("admin.dashboard.stat.feishu.hint")}
        />
        <StatCard
          label={t("admin.dashboard.stat.active.label")}
          value={stats.active}
          hint={t("admin.dashboard.stat.active.hint")}
        />
        <StatCard
          label={t("admin.dashboard.stat.suspendedArchived.label")}
          value={stats.suspended + stats.archived}
          hint={t("admin.dashboard.stat.suspendedArchived.hint")}
        />
      </div>

      <div className="mt-6">
        <InfoBanner>{phaseHint}</InfoBanner>
      </div>
    </div>
  );
}
